// Component for handling adding a virtual test printer
import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Printer } from '~reducers';
import { useButton, useDispatch, useSelector } from '~Hooks';
import { addVirtualTestPrinter } from '~actions';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { Box, GreenButton } from '~Styles';
import { handyBreakpoint } from '~Styles/breakpoints';

const Label = styled.div`
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  text-align: right;

  ${handyBreakpoint} {
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
  }

  span {
    margin-right: 16px;
    margin-bottom: 8px;
  }
`;

const Station = styled.button<{ selected: boolean }>`
  background-color: ${({ selected }) => (selected ? 'var(--colorUIGreen)' : 'transparent')};
  border: 1px solid var(--colorWhite);
  padding: 4px 8px;
  border-radius: 4px;
  color: var(--colorWhite);
  text-align: center;
  border-color: ${({ selected }) => (selected ? 'var(--colorUIGreen)' : 'var(--colorWhite)')};
  opacity: ${({ selected }) => (selected ? 1 : 0.5)};
`;

const VirtualTestPrinterForm = () => {
  const dispatch = useDispatch();
  const availableStations = useSelector((state) => state.stations);
  const [printerName, setPrinterName] = useState('Test1');
  const [isPrimary, setIsPrimary] = useState(true);
  const [selectedStations, setSelectedStations] = useState<string[]>(
    availableStations?.map((station) => station.id) ?? [],
  );

  useEffect(() => {
    if (availableStations && selectedStations.length === 0) {
      setSelectedStations(availableStations.map((station) => station.id));
    }
  }, [availableStations]);

  const handlePrinterNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPrinterName(event.target.value);
  };

  const handleSubmit = () => {
    const printerPayload: Printer = {
      id: printerName,
      coverOpen: false,
      createdAt: new Date().toISOString(),
      lastPrinted: null,
      model: 'VIRTUAL_TEST',
      online: true,
      paperEmpty: false,
      paperNearEmpty: false,
      primary: isPrimary,
      stations: selectedStations,
    };

    dispatch(addVirtualTestPrinter(printerPayload));
    setPrinterName('Test');
    setIsPrimary(false);
  };

  const handleCloudSubmit = () => {
    const printerPayload: Printer = {
      id: '00:11:62:31:34:c8',
      coverOpen: false,
      createdAt: new Date().toISOString(),
      lastPrinted: null,
      model: 'StarCloudPrinter',
      online: true,
      paperEmpty: false,
      paperNearEmpty: false,
      primary: isPrimary,
      stations: selectedStations,
    };

    dispatch(addVirtualTestPrinter(printerPayload));
    setIsPrimary(false);
  };

  const handleFaultySubmit = () => {
    // Create a faulty printer that will generate print errors in the queue
    const printerPayload: Printer = {
      id: 'FAULTY_ERROR_PRINTER', // Special ID that we'll intercept
      coverOpen: false,
      createdAt: new Date().toISOString(),
      lastPrinted: null,
      model: 'FAULTY_TEST', // Special model to identify this printer
      online: true, // Keep online so prints are attempted
      paperEmpty: false,
      paperNearEmpty: false,
      primary: isPrimary,
      stations: selectedStations,
    };

    dispatch(addVirtualTestPrinter(printerPayload));
    setIsPrimary(false);
  };

  const addVirtualButton = useButton({
    onClick: handleSubmit,
  });

  const addCloudButton = useButton({
    onClick: handleCloudSubmit,
  });

  const addFaultyPrinter = useButton({
    onClick: handleFaultySubmit,
  });

  return (
    <Box marginBottom="large">
      <Heading level={3} bottom={8} color="#fff">
        [DEBUG] Add virtual test printer - only shown in development
      </Heading>
      <Paragraph level={3} color="#fff">
        Note: This printer will disappear when reloading/hot reloading. It lives in the redux state
        and is not persisted right now.
      </Paragraph>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <Box display="flex" justifyContent="space-between" marginTop="large">
          <Label>
            <span>Printer name:</span>
            <input type="text" value={printerName} onChange={handlePrinterNameChange} />
          </Label>
          <Label>
            <span>Is primary:</span>
            <input type="checkbox" checked={isPrimary} onChange={() => setIsPrimary(!isPrimary)} />
          </Label>
        </Box>
        <Label>
          <span style={{ whiteSpace: 'nowrap' }}>Selected stations:</span>
          {/* Checkboxes with all stations */}
          <Box display="flex" gap="small" flexWrap="wrap">
            {availableStations &&
              availableStations.map((station) => (
                <Station
                  key={station.id}
                  selected={selectedStations.includes(station.id)}
                  onTouchStart={() => {
                    if (selectedStations.includes(station.id)) {
                      setSelectedStations(selectedStations.filter((id) => id !== station.id));
                    } else {
                      setSelectedStations([...selectedStations, station.id]);
                    }
                  }}>
                  <span>{station.name}</span>
                </Station>
              ))}
          </Box>
        </Label>
        <Box marginTop="medium" display="flex" gap="small">
          <GreenButton {...addVirtualButton}>Add virtual printer</GreenButton>
          <GreenButton {...addCloudButton}>Add cloud printer</GreenButton>
          <GreenButton {...addFaultyPrinter}>Add offline printer</GreenButton>
        </Box>
      </div>
    </Box>
  );
};

export default VirtualTestPrinterForm;
